import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StatCard } from '@/components/common/StatCard'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { AlertCard } from '@/components/alert/AlertCard'
import { DeviceCard } from '@/components/device/DeviceCard'
import { DeviceMap } from '@/components/map/DeviceMap'
import { TankLevelChart } from '@/components/charts/TankLevelChart'
import { RainfallChart } from '@/components/charts/RainfallChart'
import { useAuthStore } from '@/stores/authStore'
import { useDashboard } from '@/hooks/useDashboard'
import { weatherService } from '@/services/weather.service'
import { useQuery } from '@tanstack/react-query'
import { Activity, AlertTriangle, Building2, Server, ArrowRight, Droplets, Wind, Thermometer } from 'lucide-react'
import { Link } from 'react-router-dom'

export const DashboardPage = () => {
  const { user } = useAuthStore()
  const dashboardColumns = user?.preferences?.dashboardColumns || '3'
  const gridColsClass = {
    '1': 'grid-cols-1',
    '2': 'grid-cols-1 md:grid-cols-2',
    '3': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    '4': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[dashboardColumns]
  const { data: dashboard, isLoading, error } = useDashboard()

  const showWeather = user?.preferences?.showWeatherWidget && user?.preferences?.weatherAccountId
  const { data: weather, error: weatherError } = useQuery({
    queryKey: ['dashboard-weather', user?.preferences?.weatherAccountId],
    queryFn: () => weatherService.getCurrentWeather(user!.preferences.weatherAccountId!),
    enabled: !!showWeather,
    staleTime: 10 * 60 * 1000, // 10 min
    retry: false,
  })

  const kelvinToCelsius = (k: number) => Math.round(k - 273.15)

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return <ErrorMessage message="Failed to load dashboard data. Please try again." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.displayName || user?.firstName}!
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={`grid gap-4 ${gridColsClass}`}>
        <StatCard
          title="Total Devices"
          value={dashboard?.totalDevices}
          icon={Server}
          variant="default"
        />
        <StatCard
          title="Online (24h)"
          value={dashboard?.devicesOnline24h}
          icon={Activity}
          variant="success"
        />
        <StatCard
          title="Active Alerts"
          value={dashboard?.activeAlerts}
          icon={AlertTriangle}
          variant={dashboard?.activeAlerts && dashboard.activeAlerts > 0 ? 'warning' : 'default'}
        />
        <StatCard
          title="Accounts"
          value={dashboard?.accountCount}
          icon={Building2}
          variant="default"
        />
      </div>

      {/* Weather Widget */}
      {showWeather && weatherError && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-destructive text-center">
              Failed to load weather data. Please try refreshing.
            </p>
          </CardContent>
        </Card>
      )}
      {showWeather && weather?.current && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {weather.current.weather?.[0]?.icon && (
                  <img
                    src={`https://openweathermap.org/img/wn/${weather.current.weather[0].icon}@2x.png`}
                    alt={weather.current.weather[0].description}
                    className="h-12 w-12"
                  />
                )}
                <div>
                  <p className="text-2xl font-bold">{kelvinToCelsius(weather.current.temp)}&deg;C</p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {weather.current.weather?.[0]?.description || 'Unknown'}
                  </p>
                </div>
              </div>
              <div className="flex gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Thermometer className="h-4 w-4" />
                  Feels {kelvinToCelsius(weather.current.feels_like)}&deg;C
                </div>
                <div className="flex items-center gap-1">
                  <Droplets className="h-4 w-4" />
                  {weather.current.humidity}%
                </div>
                <div className="flex items-center gap-1">
                  <Wind className="h-4 w-4" />
                  {Math.round(weather.current.wind_speed * 3.6)} km/h
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className={`grid gap-6 ${gridColsClass}`}>
        {/* Rainfall Chart */}
        <Card>
          <CardContent className="pt-6">
            <RainfallChart devices={dashboard?.recentDevices || []} />
          </CardContent>
        </Card>

        {/* Tank Level Chart */}
        <Card>
          <CardContent className="pt-6">
            <TankLevelChart devices={dashboard?.recentDevices || []} />
          </CardContent>
        </Card>
      </div>

      {/* Map and Devices Table Row */}
      <div className={`grid gap-6 ${gridColsClass}`}>
        {/* Device Map */}
        <Card>
          <CardHeader>
            <CardTitle>Device Locations</CardTitle>
          </CardHeader>
          <CardContent>
            <DeviceMap devices={dashboard?.recentDevices || []} />
          </CardContent>
        </Card>

        {/* Recent Devices */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Your Devices</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to="/devices">
                  View All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {dashboard?.recentDevices && dashboard.recentDevices.length > 0 ? (
              dashboard.recentDevices.slice(0, 5).map((device) => (
                <DeviceCard key={device.id} device={device} />
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No devices found
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Alerts Row */}
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Alerts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {dashboard?.recentAlerts && dashboard.recentAlerts.length > 0 ? (
              dashboard.recentAlerts.slice(0, 5).map((alert) => (
                <AlertCard key={alert.id} alert={alert} />
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No recent alerts
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
